/**
 * Knowledge Base Service - Handles API interactions for knowledge base management
 */

import axios from 'axios';
import { post, put } from '../utils/apiUtils';

// No need to use BASE_URL as it's already configured in apiUtils.js
const API_URL = `/api/v1/persistence/knowledgebase`;

/**
 * Get all knowledge bases with optional filtering
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const getKnowledgeBases = async (filter = {}, sort ={}, query = null) => {
  try {
    const response = await post(`${API_URL}/fetch?${query}`, { filter: filter,sort });
    return response;
  } catch (error) {
    console.error('Error fetching knowledge bases:', error);
    throw error;
  }
};

/**
 * Get a specific knowledge base by ID
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @returns {Promise<Object>} Promise resolving to knowledge base object
 */
export const getKnowledgeBaseById = async (knowledgeBaseId) => {
  try {
    const response = await axios.get(`${API_URL}/${knowledgeBaseId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching knowledge base ${knowledgeBaseId}:`, error);
    throw error;
  }
};

/**
 * Create a new knowledge base
 * @param {Object} knowledgeBaseData - Knowledge base data
 * @returns {Promise<Object>} Promise resolving to created knowledge base
 */
export const createKnowledgeBase = async (knowledgeBaseData) => {
  try {
    const response = await post(`${API_URL}`, knowledgeBaseData);
    return response.data;
  } catch (error) {
    console.error('Error creating knowledge base:', error);
    throw error;
  }
};

/**
 * Update an existing knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {Object} knowledgeBaseData - Updated knowledge base data
 * @returns {Promise<Object>} Promise resolving to updated knowledge base
 */
export const updateKnowledgeBase = async ( knowledgeBaseData) => {
  try {
    const response = await put(`${API_URL}`, knowledgeBaseData);
    return response.data;
  } catch (error) {
    console.error(`Error updating knowledge base:`, error);
    throw error;
  }
};

/**
 * Delete a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @returns {Promise<void>} Promise resolving when knowledge base is deleted
 */
export const deleteKnowledgeBase = async (knowledgeBaseId) => {
  try {
    await axios.delete(`${API_URL}/${knowledgeBaseId}`);
  } catch (error) {
    console.error(`Error deleting knowledge base ${knowledgeBaseId}:`, error);
    throw error;
  }
};

/**
 * Get documents in a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of documents
 */
export const getKnowledgeBaseDocuments = async (knowledgeBaseId, filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/${knowledgeBaseId}/documents`, { params: filters });
    return response.data;
  } catch (error) {
    console.error(`Error fetching documents for knowledge base ${knowledgeBaseId}:`, error);
    throw error;
  }
};

/**
 * Get a specific document from a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {string} documentId - Document ID
 * @returns {Promise<Object>} Promise resolving to document object
 */
export const getKnowledgeBaseDocument = async (knowledgeBaseId, documentId) => {
  try {
    const response = await axios.get(`${API_URL}/${knowledgeBaseId}/documents/${documentId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching document ${documentId} from knowledge base ${knowledgeBaseId}:`, error);
    throw error;
  }
};

/**
 * Add a document to a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {Object} documentData - Document data
 * @returns {Promise<Object>} Promise resolving to added document
 */
export const addKnowledgeBaseDocument = async (knowledgeBaseId, documentData) => {
  try {
    const response = await axios.post(`${API_URL}/${knowledgeBaseId}/documents`, documentData);
    return response.data;
  } catch (error) {
    console.error(`Error adding document to knowledge base ${knowledgeBaseId}:`, error);
    throw error;
  }
};

/**
 * Update a document in a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {string} documentId - Document ID
 * @param {Object} documentData - Updated document data
 * @returns {Promise<Object>} Promise resolving to updated document
 */
export const updateKnowledgeBaseDocument = async (knowledgeBaseId, documentId, documentData) => {
  try {
    const response = await axios.put(`${API_URL}/${knowledgeBaseId}/documents/${documentId}`, documentData);
    return response.data;
  } catch (error) {
    console.error(`Error updating document ${documentId} in knowledge base ${knowledgeBaseId}:`, error);
    throw error;
  }
};

/**
 * Delete a document from a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {string} documentId - Document ID
 * @returns {Promise<void>} Promise resolving when document is deleted
 */
export const deleteKnowledgeBaseDocument = async (knowledgeBaseId, documentId) => {
  try {
    await axios.delete(`${API_URL}/${knowledgeBaseId}/documents/${documentId}`);
  } catch (error) {
    console.error(`Error deleting document ${documentId} from knowledge base ${knowledgeBaseId}:`, error);
    throw error;
  }
};

/**
 * Upload a file to a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {FormData} formData - Form data containing the file
 * @returns {Promise<Object>} Promise resolving to uploaded document
 */
export const uploadKnowledgeBaseFile = async (knowledgeBaseId, formData) => {
  try {
    const response = await axios.post(`${API_URL}/${knowledgeBaseId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error uploading file to knowledge base ${knowledgeBaseId}:`, error);
    throw error;
  }
};

/**
 * Get knowledge base usage statistics
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} Promise resolving to usage statistics
 */
export const getKnowledgeBaseUsage = async (knowledgeBaseId, filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/${knowledgeBaseId}/usage`, { params: filters });
    return response.data;
  } catch (error) {
    console.error(`Error fetching usage for knowledge base ${knowledgeBaseId}:`, error);
    throw error;
  }
};


/**
 * Get knowledge base usage statistics
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} Promise resolving to usage statistics
 */
export const fetchKnowledgeBaseCount = async () => {
  try {
    const response = await post(`${API_URL}/count`, {});
    return response.count;
  } catch (error) {
    console.error(`Error fetching usage for knowledge base count:`, error);
    throw error;
  }
};


/**
 * Get knowledge base usage statistics
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} Promise resolving to usage statistics
 */
export const fetchKnowledgeBaseByPagination = async (skip, limit) => {
  try {
    const response = await post(`${API_URL}/fetch?skip=${skip}&limit=${limit}`, {});
    return response;
  } catch (error) {
    console.error(`Error fetching usage for knowledge base pagination:`, error);
    throw error;
  }
};




export default {
  getKnowledgeBases,
  getKnowledgeBaseById,
  createKnowledgeBase,
  updateKnowledgeBase,
  deleteKnowledgeBase,
  getKnowledgeBaseDocuments,
  getKnowledgeBaseDocument,
  addKnowledgeBaseDocument,
  updateKnowledgeBaseDocument,
  deleteKnowledgeBaseDocument,
  uploadKnowledgeBaseFile,
  getKnowledgeBaseUsage
};
